import { Palette } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShellNotice } from './shell-notice';

/** `localStorage` flag gating the one-time migration notice (Resolution 19) — set on dismissal,
 * never reset, so the banner never reappears in the same browser once closed. */
const WHATS_NEW_DISMISSED_KEY = 'dgb-console:whats-new-theme-i18n-dismissed';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(WHATS_NEW_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * One-time migration notice (SL-0027, Task 12.4, Resolution 19), as a shell notice (#956):
 * announces the theme/language settings this StepLedger ships, causally explaining WHY the
 * interface may already look different (`theme_preference` defaults to `'system'`, Resolution
 * 7/19) rather than only naming where the new settings live. None once dismissed for this browser.
 */
export function useThemeNotice(): ShellNotice | null {
  const { t } = useTranslation('shell');
  const [dismissed, setDismissed] = useState(() => isDismissed());
  if (dismissed) return null;
  return {
    id: 'theme',
    label: t('whatsNew.label'),
    text: t('whatsNew.themeAndLanguage'),
    icon: Palette,
    details: t('whatsNew.themeAndLanguage'),
    dismiss: () => {
      try {
        localStorage.setItem(WHATS_NEW_DISMISSED_KEY, 'true');
      } catch {
        // Storage refused (private mode): the notice closes for this visit only.
      }
      setDismissed(true);
    },
  };
}
